import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import { TagAxios } from "../../axios/tag/tag.axios";
import { Tag } from "../../model/tag/Tag";
import { CreateTagDto } from "../../model/tag/dto/CreateTagDto";
import { DeleteTagDto } from "../../model/tag/dto/DeleteTagDto";
import { GetTagUsageDto } from "../../model/tag/dto/GetTagUsageDto";
import { GetTagUsageResponseDto } from "../../model/tag/dto/GetTagUsageResponseDto";
import { UpdateTagDto } from "../../model/tag/dto/UpdateTagDto";
import { AtiraThunk } from "../AtiraThunk";
import { revertAllReduxAction } from "../store-reset";
import { userActions } from "../user/user.slice";

interface TagReducer {
  tags: Tag[];
  tagsLoading: boolean;
}

const initialState = Object.freeze<TagReducer>({
  tags: [],
  tagsLoading: false,
});

const byName = (a: Tag, b: Tag) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

const getTags = AtiraThunk<Tag[], void>("/tag", TagAxios.getTags);

const createTag = AtiraThunk<Tag, CreateTagDto>(
  "/tag/create",
  TagAxios.createTag,
);

const updateTag = AtiraThunk<Tag, UpdateTagDto>(
  "/tag/:id/update",
  TagAxios.updateTag,
);

const getTagUsage = AtiraThunk<GetTagUsageResponseDto, GetTagUsageDto>(
  "/tag/:id/usage",
  TagAxios.getTagUsage,
);

const deleteTag = AtiraThunk<void, DeleteTagDto>(
  "/tag/:id/delete",
  TagAxios.deleteTag,
);

const tagSlice = createSlice({
  name: "tag",
  initialState,
  reducers: {},
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(getTags.pending, (state) => {
      state.tagsLoading = true;
    });
    addCase(getTags.fulfilled, (state, action) => {
      state.tags = action.payload;
      state.tagsLoading = false;
    });
    addCase(getTags.rejected, (state) => {
      state.tagsLoading = false;
    });

    addCase(createTag.fulfilled, (state, action) => {
      state.tags = [...state.tags, action.payload].sort(byName);
    });

    addCase(updateTag.fulfilled, (state, action) => {
      state.tags = state.tags
        .map((tag) => (tag._id === action.payload._id ? action.payload : tag))
        .sort(byName);
    });

    addCase(deleteTag.fulfilled, (state, action) => {
      state.tags = state.tags.filter(
        (tag) => tag._id !== action.meta.arg.tagId,
      );
    });

    addMatcher(isAnyOf(userActions.logout, revertAllReduxAction), () => {
      return initialState;
    });
  },
});

export const tagActions = {
  ...tagSlice.actions,
  getTags,
  createTag,
  updateTag,
  getTagUsage,
  deleteTag,
};

export default tagSlice.reducer;
